import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { find, first, fromPairs, isBoolean, isEmpty, keyBy, map, startCase, toString } from 'lodash';
import React from 'react';
import { getModel } from 'api/platform';
import CopyableText from 'components/atoms/CopyableText';
import { UserIdentity } from 'components/atoms/UserIdentity';
import { useLocation } from 'react-router-dom';
import {
  ExternalSlideTransferJob,
  getSecondaryAnalysisJobPrimaryRunOrchestrationId,
  InferenceParams,
  Job,
  JobStatus,
  JobType,
} from 'interfaces/job';
import { formatDateTimeForUser } from 'utils/dateTime';
import { getModelId } from 'utils/models';
import { useUiSettings } from 'utils/queryHooks/uiConstantsHooks';
import { useStudies } from 'utils/queryHooks/useStudies';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { ExternalSlideTransferJobDetailsSection } from './ExternalSlideTransferJobDetailsSection';
import { classNamesOptions, modelTypeCell, modelTypeOptions } from './inferenceFieldsOptions';
import { IngestJobDetailsSection } from './IngestJobDetailsSection';
import { JobArchiveControl } from './JobArchiveControl';
import { JobDetailsButton } from './JobDetailsLink';
import { getJobOutcomeSummary } from './jobOutcomeSummary';
import { JobParamsJsonTree } from './JobParamsJsonTree';
import { getJobStatusDisplayLabel, JobStatusChip } from './JobStatusChip';
import { JobStepHistorySection } from './JobStepHistorySection';
import { PrepareCustomerResultsJobParams } from './PrepareCustomerResultsJobParams';
import { SubtaskDetails } from './SubtaskDetails';
import { TileGenerationJobDetailsSection } from './TileGenerationJobDetailsSection';
import { stringToDisplay } from './utils';

const SKIP = 'skip';

type JobWithBackendMetadata = Partial<Job> & {
  params?: Record<string, unknown>;
  results?: Record<string, unknown>;
};

const getJobAwsRefs = (job?: Partial<Job>) => {
  const { params, results } = (job || {}) as JobWithBackendMetadata;
  const rawAwsRefs = params?.aws_refs || params?.awsRefs || results?.aws_refs || results?.awsRefs;

  if (!rawAwsRefs || typeof rawAwsRefs !== 'object' || Array.isArray(rawAwsRefs)) {
    return [];
  }

  return Object.entries(rawAwsRefs as Record<string, unknown>).filter(([, value]) => !isEmpty(toString(value)));
};

const JobRunIdentifiers: React.FC<{
  job: Partial<Job>;
  primaryRunOrchestrationId?: string;
  hasExternalDetails: boolean;
  awsRefs: Array<[string, unknown]>;
}> = ({ job, primaryRunOrchestrationId, hasExternalDetails, awsRefs }) => (
  <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      aria-controls="job-run-identifiers-content"
      id="job-run-identifiers-header"
    >
      <Typography variant="h4">Run identifiers</Typography>
    </AccordionSummary>
    <AccordionDetails id="job-run-identifiers-content">
      <Grid container direction="column" spacing={1}>
        {job.parentId && (
          <CopyableText variant="body2" sx={{ wordBreak: 'break-word' }}>
            Parent job: {job.parentId}
          </CopyableText>
        )}
        {job.orchestrationId && (
          <CopyableText variant="body2" sx={{ wordBreak: 'break-word' }}>
            Orchestration: {job.orchestrationId}
          </CopyableText>
        )}
        {primaryRunOrchestrationId && (
          <CopyableText variant="body2" sx={{ wordBreak: 'break-word' }}>
            Primary run: {primaryRunOrchestrationId}
          </CopyableText>
        )}
        {hasExternalDetails ? (
          <Grid item container direction="column" spacing={0.75}>
            {awsRefs.length > 0 && (
              <Grid item>
                <Typography variant="body2" color="text.secondary">
                  AWS References:
                </Typography>
              </Grid>
            )}
            {job.externalTaskId && (
              <Grid item container spacing={1}>
                <Grid item>
                  <Typography variant="body2">Task ID:</Typography>
                </Grid>
                <Grid item xs>
                  <CopyableText variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {job.externalTaskId}
                  </CopyableText>
                </Grid>
              </Grid>
            )}
            {job.externalTaskLink && (
              <Grid item>
                <MuiLink color="inherit" underline="hover" href={job.externalTaskLink} target="_blank">
                  External task link
                </MuiLink>
              </Grid>
            )}
            {map(awsRefs, ([key, value]) => (
              <Grid item container spacing={1} key={key}>
                <Grid item>
                  <Typography variant="body2" color="text.secondary">
                    {startCase(key)}:
                  </Typography>
                </Grid>
                <Grid item xs>
                  <CopyableText variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {toString(value)}
                  </CopyableText>
                </Grid>
              </Grid>
            ))}
          </Grid>
        ) : null}
      </Grid>
    </AccordionDetails>
  </Accordion>
);

type JobDetailsTab = 'parameters' | 'subtasks';

const JobDetailsTabPanel: React.FC<React.PropsWithChildren<{ activeTab: JobDetailsTab; tab: JobDetailsTab }>> = ({
  activeTab,
  tab,
  children,
}) =>
  activeTab === tab ? (
    <Grid item role="tabpanel" id={`job-details-${tab}-panel`} aria-labelledby={`job-details-${tab}-tab`}>
      {children}
    </Grid>
  ) : null;

interface JobDataProps {
  job?: Partial<Job>;
  isLoading?: boolean;
  onClosed?: React.MouseEventHandler<HTMLButtonElement>;
  onJobArchived?: () => void;
}

export const JobDetails: React.FC<JobDataProps> = ({ job, isLoading = false, onClosed, onJobArchived }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = React.useState<JobDetailsTab>('parameters');
  const showDrawerCloseButton = Boolean(onClosed);
  const location = useLocation();
  const parentJobId =
    new URLSearchParams(location.search).get('jobParentId') || (!isLoading ? job?.parentId : undefined);
  const { labId } = useCurrentLabId();

  const { data: studies } = useStudies(labId);
  const modelPath = job?.type === JobType.Inference ? first((job?.params as InferenceParams)?.modelPaths) : undefined;
  const modelId = getModelId(modelPath || '');
  const { data: modelData } = useQuery({
    queryKey: ['model', modelId],
    queryFn: ({ signal }) => getModel(modelId || '', signal),
    enabled: job?.type === JobType.Inference && Boolean(modelId),
  });

  const { uiSettings } = useUiSettings();
  const currStudy = job?.studyId ? find(studies, (study) => job.studyId === study.id)?.name : undefined;

  const requestSenderToDisplayText = fromPairs(
    map(uiSettings?.enumDisplayNames?.['requestSender'], ({ value, label }) => [value, label]) || []
  );
  const jobStatusToDisplayText = fromPairs(
    map(uiSettings?.enumDisplayNames?.['jobStatus'], ({ value, label }) => [value, label]) || []
  );
  jobStatusToDisplayText[SKIP] = 'Skipped';

  const modelTypeOptionsByValue = keyBy(modelTypeOptions, 'value');
  const classNamesOptionsByValue = keyBy(classNamesOptions, 'value');

  const assignmentIds =
    job?.type === JobType.Inference ? stringToDisplay((job?.params as InferenceParams)?.assignmentIds) : '';
  const classNames =
    job?.type === JobType.Inference
      ? stringToDisplay(
          map(
            (job?.params as InferenceParams)?.classNames,
            (className) => classNamesOptionsByValue[className]?.text || className
          )
        )
      : '';
  // heatmapNames and modelPaths  saved as array but currently there is only one heatmap and model per job
  const modelPaths =
    job?.type === JobType.Inference ? stringToDisplay((job?.params as InferenceParams)?.modelPaths) : '';
  const heatmapNames =
    job?.type === JobType.Inference ? stringToDisplay((job?.params as InferenceParams)?.heatmapNames) : '';

  const runType = job?.type === JobType.Inference ? (job?.params as InferenceParams)?.runType : undefined;

  const tissueMaskFromAnnotations =
    job?.type === JobType.Inference ? (job?.params as InferenceParams)?.tissueMaskFromAnnotations : undefined;

  const tissueSegmentationModelOverride =
    job?.type === JobType.Inference ? (job?.params as InferenceParams)?.tissueSegmentationModelOverride : undefined;
  const awsRefs = getJobAwsRefs(job);
  const hasExternalDetails = Boolean(job?.externalTaskId || job?.externalTaskLink || !isEmpty(awsRefs));
  const ingestJob = job?.type === JobType.Ingest ? job : undefined;
  const ingestJobId = ingestJob?.id;
  const shouldShowIngestDetails =
    Boolean(ingestJobId) && ingestJob?.status !== JobStatus.Pending && ingestJob?.status !== JobStatus.Running;
  const jobOutcomeSummary = getJobOutcomeSummary(job || {});
  const primaryRunOrchestrationId = getSecondaryAnalysisJobPrimaryRunOrchestrationId(job);
  const isParentJob = !isLoading && job?.isParentJob === true;
  const isJobMetadataLoading = isLoading;
  const selectedTab = isParentJob ? activeTab : 'parameters';
  const hasRunIdentifiers = Boolean(
    job?.parentId || job?.orchestrationId || primaryRunOrchestrationId || hasExternalDetails
  );

  return (
    <Grid container p={2} direction="column" spacing={2} flexWrap="nowrap">
      <Grid
        container
        item
        data-testid="job-details-sticky-header"
        alignItems="flex-start"
        wrap="nowrap"
        columnSpacing={1}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Grid item xs sx={{ minWidth: 0 }}>
          {parentJobId && (
            <JobDetailsButton
              jobId={parentJobId}
              color="inherit"
              size="small"
              variant="text"
              startIcon={<ArrowBackIcon fontSize="small" />}
              sx={{ mb: 1, pl: 0 }}
            >
              Back to parent job
            </JobDetailsButton>
          )}
          {isJobMetadataLoading ? (
            <Skeleton width="65%" height={40} />
          ) : (
            <CopyableText variant="h3" sx={{ wordBreak: 'break-word' }}>
              {job?.name || job?.orchestrationId}
            </CopyableText>
          )}
          {!isJobMetadataLoading && Boolean(job?.name) && job?.id && (
            <CopyableText
              variant="h5"
              color={theme.palette.text.secondary}
              sx={{ wordBreak: 'break-word' }}
              textForCopy={job?.id}
            >
              {job?.id}
            </CopyableText>
          )}
        </Grid>
        <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {!isJobMetadataLoading && job?.id && (
            <JobArchiveControl
              job={{
                id: job.id,
                name: job.name,
                status: job.status,
                userId: job.userId,
                deletedAt: job.deletedAt,
              }}
              labeled
              contained
              onArchived={onJobArchived}
            />
          )}
          {showDrawerCloseButton && (
            <IconButton onClick={onClosed} aria-label="Close job details" sx={{ p: 0 }}>
              <CloseIcon />
            </IconButton>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ pt: 2 }} />

      {isJobMetadataLoading ? (
        <Grid item>
          <Skeleton width="80%" />
        </Grid>
      ) : job?.description ? (
        <Grid item>
          <Typography variant="body1">{job.description}</Typography>
        </Grid>
      ) : null}

      <Grid item>
        <Box
          component="section"
          aria-label="Job summary"
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(135px, 1fr))',
            columnGap: 2,
            rowGap: 1.25,
          }}
        >
          {isJobMetadataLoading ? (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Status
              </Typography>
              <Skeleton variant="rounded" width={104} height={24} />
            </Box>
          ) : jobOutcomeSummary.displayStatus ? (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Status
              </Typography>
              <JobStatusChip
                status={jobOutcomeSummary.displayStatus}
                label={
                  jobOutcomeSummary.displayLabel ||
                  getJobStatusDisplayLabel(jobOutcomeSummary.displayStatus, jobStatusToDisplayText)
                }
              />
            </Box>
          ) : null}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Started at
            </Typography>
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
              {isJobMetadataLoading ? (
                <Skeleton width={120} />
              ) : (
                formatDateTimeForUser(job?.startedAt) || 'Not recorded'
              )}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Source
            </Typography>
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
              {isJobMetadataLoading ? (
                <Skeleton width={120} />
              ) : (
                requestSenderToDisplayText[job?.requestSender || ''] || 'Not recorded'
              )}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Run by
            </Typography>
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
              {isJobMetadataLoading ? (
                <Skeleton width={140} />
              ) : (
                <UserIdentity userId={job?.userId} displayName={job?.submittedBy} usersById={job?.usersById} />
              )}
            </Typography>
          </Box>
          {!isJobMetadataLoading && job?.deletedAt && (
            <>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Archived at
                </Typography>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  {formatDateTimeForUser(job.deletedAt)}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Archived by
                </Typography>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  <UserIdentity userId={job.deletedBy} usersById={job.usersById} />
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Grid>
      {!isJobMetadataLoading && job?.statusMessage && (
        <Grid item>
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {job.statusMessage}
          </Typography>
        </Grid>
      )}
      {!isJobMetadataLoading && job ? <JobStepHistorySection job={job} /> : null}
      {!isJobMetadataLoading && shouldShowIngestDetails && ingestJobId && ingestJob ? (
        <IngestJobDetailsSection jobId={ingestJobId} job={ingestJob} />
      ) : null}
      {!isJobMetadataLoading && job?.type === JobType.TileGeneration && <TileGenerationJobDetailsSection job={job} />}
      {!isJobMetadataLoading && job?.type === JobType.ExternalSlideTransfer && (
        <ExternalSlideTransferJobDetailsSection job={job as ExternalSlideTransferJob} />
      )}
      {!isJobMetadataLoading && hasRunIdentifiers && (
        <Grid item>
          <JobRunIdentifiers
            job={job || {}}
            primaryRunOrchestrationId={primaryRunOrchestrationId}
            hasExternalDetails={hasExternalDetails}
            awsRefs={awsRefs}
          />
        </Grid>
      )}
      <Grid item>
        <Tabs
          value={selectedTab}
          onChange={(_event, tab: JobDetailsTab) => setActiveTab(tab)}
          aria-label="Job details"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            label="Parameters"
            value="parameters"
            id="job-details-parameters-tab"
            aria-controls="job-details-parameters-panel"
          />
          {isParentJob && (
            <Tab
              label="Sub tasks"
              value="subtasks"
              id="job-details-subtasks-tab"
              aria-controls="job-details-subtasks-panel"
            />
          )}
        </Tabs>
      </Grid>
      <JobDetailsTabPanel activeTab={selectedTab} tab="parameters">
        <Grid item>
          {isJobMetadataLoading ? (
            <Skeleton variant="rectangular" height={150} />
          ) : (
            <Grid container direction="column" spacing={1}>
              {heatmapNames && (
                <Grid item container spacing={1}>
                  <Grid item>
                    <Typography variant="body1">Heatmap name: </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body1"> {heatmapNames}</Typography>
                  </Grid>
                </Grid>
              )}
              <Grid item container spacing={1}>
                <Grid item>
                  <Typography variant="body1">Study: </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1"> {currStudy}</Typography>
                </Grid>
              </Grid>
              {runType && (
                <Grid item container spacing={1}>
                  <Grid item>
                    <Typography variant="body1">Model type: </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body1">{modelTypeOptionsByValue[runType]?.text || runType}</Typography>
                  </Grid>
                </Grid>
              )}
              {job?.type === JobType.Inference && (
                <Grid item container spacing={1}>
                  <Grid item>
                    <Typography variant="body1">Model: </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Tooltip placement="top-start" title={modelPaths}>
                      <Typography variant="body1" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {modelPaths}
                      </Typography>
                    </Tooltip>
                  </Grid>
                </Grid>
              )}
              {modelData?.meta?.modelType === modelTypeCell.apiModelValue && (
                <>
                  <Grid item container spacing={1}>
                    <Grid item>
                      <Typography variant="body1">dedup Value: </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body1">{(job?.params as InferenceParams)?.dedupValue}</Typography>
                    </Grid>
                  </Grid>
                  <Grid item container spacing={1}>
                    <Grid item>
                      <Typography variant="body1">Use Dynamic Cell Detection: </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body1">
                        {isBoolean((job?.params as InferenceParams)?.useDynamicCellDetection) &&
                          startCase(toString((job?.params as InferenceParams)?.useDynamicCellDetection))}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid item container spacing={1}>
                    <Grid item>
                      <Typography variant="body1">Dynamic Cell Detection Config: </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body1">
                        {(job?.params as InferenceParams)?.dynamicCellDetectionConfig}
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              )}
              {job?.type === JobType.Inference && (
                <Grid item container spacing={1}>
                  <Grid item>
                    <Typography variant="body1">ROI mask: </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body1">
                      {isBoolean(tissueMaskFromAnnotations) && startCase(toString(tissueMaskFromAnnotations))}
                    </Typography>
                  </Grid>
                </Grid>
              )}
              {!isEmpty(tissueSegmentationModelOverride) && (
                <Grid item container spacing={1}>
                  <Grid item>
                    <Typography variant="body1">TSM Model: </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Tooltip placement="top-start" title={tissueSegmentationModelOverride}>
                      <Typography variant="body1" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {tissueSegmentationModelOverride}
                      </Typography>
                    </Tooltip>
                  </Grid>
                </Grid>
              )}
              {tissueMaskFromAnnotations && (
                <Grid>
                  <Grid item container spacing={1}>
                    <Grid item>
                      <Typography variant="body1">Assignments Ids: </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body1">{assignmentIds}</Typography>
                    </Grid>
                  </Grid>
                  <Grid item container spacing={1}>
                    <Grid item>
                      <Typography variant="body1">Class names: </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body1">{classNames}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {job?.type === JobType.PrepareCustomerResults && <PrepareCustomerResultsJobParams job={job} />}
              {job?.params && (
                <Grid item>
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body1">Raw Parameters Data</Typography>
                      <CopyableText
                        textForCopy={JSON.stringify(job?.params)}
                        sx={{ ml: 1 }}
                        tooltipText="Copy Raw Parameters Data"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </CopyableText>
                    </AccordionSummary>
                    <AccordionDetails>
                      <JobParamsJsonTree data={job?.params} />
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      </JobDetailsTabPanel>
      {isParentJob && (
        <JobDetailsTabPanel activeTab={selectedTab} tab="subtasks">
          <SubtaskDetails job={job || {}} isLoading={isLoading} />
        </JobDetailsTabPanel>
      )}
    </Grid>
  );
};
